import { SystemStyleObject, Text } from "@chakra-ui/react";
import { Select, SingleValue } from "chakra-react-select";
import { InternalAdminOrganizationFragment } from "gql/graphql";
import React, { memo, useCallback } from "react";
import colors from "app/colors";
import useInternalAdminOrganizationList from "../hooks/use-internal-admin-organization-list";
import useQueryString from "modules/hooks/use-query-string";

interface IProps {
  onOrganizationSelected: (organizationId: string) => void;
}

const InternalAdminOrganizationSelector = ({
  onOrganizationSelected,
}: IProps) => {
  const { qsOrganizationId } = useQueryString();

  const { organizations } = useInternalAdminOrganizationList({
    input: {
      limit: 100000,
      offset: 0,
    },
  });

  const _value = organizations.find((o) => o.id === qsOrganizationId);

  const formatOptionLabel = useCallback(
    (organization: InternalAdminOrganizationFragment) => (
      <Text>{organization.name}</Text>
    ),
    [],
  );

  const getOptionLabel = useCallback(
    (organization: InternalAdminOrganizationFragment) => `${organization.name}`,
    [],
  );

  const getOptionValue = useCallback(
    (organization: InternalAdminOrganizationFragment) => organization.id,
    [],
  );

  const onChange = useCallback(
    (
      organizationSingleValue: SingleValue<InternalAdminOrganizationFragment>,
    ) => {
      if (organizationSingleValue) {
        onOrganizationSelected(organizationSingleValue.id);
      }
    },
    [onOrganizationSelected],
  );

  return (
    <Select
      options={organizations}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      menuPlacement="bottom"
      onChange={onChange}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          fontSize: 12,
          color: colors.placeholderText,
          p: 0,
          m: 0,
        }),
        dropdownIndicator: (
          provided: SystemStyleObject,
        ): SystemStyleObject => ({
          ...provided,
          p: 1,
        }),
        menuList: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontSize: 12,
          color: colors.blackSecondary,
        }),
      }}
      placeholder="Select Organization"
      value={_value}
    />
  );
};

export default memo(InternalAdminOrganizationSelector);
